import { MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ScreenshotMonitorOutlined, FaceOutlined, Mic } from '@mui/icons-material';
import { useExtensionManager } from '@/hooks/useExtensionManager';
import { useAuthProtection } from '@/features/auth/useAuthProtection';
import { useFirstNoteNudgeStore } from '@/stores/firstNoteNudgeStore';
import NewNoteModal from '@/components/CustomModal/NewNoteModal';
import { RouteMap } from '@/routes/Routes';

/**
 * Single "New Note" entry point in the global header.
 * Consolidates the recording options (screen record, video, audio) that used
 * to live in the sidebar Record menu and per-page buttons.
 */
const NewNoteButton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 900px and below
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const { isModalOpen, checkExtensionInstalled, onModalConfirm, setIsModalOpen } = useExtensionManager();
  const { canCreateNote } = useAuthProtection();
  const isNudgeActive = useFirstNoteNudgeStore((state) => state.isActive);

  // First-run nudge: point new users (no notes yet) at the header button.
  // Desktop only, and hidden while the dropdown is open. Matches the original
  // Home-only "Create Your First Note" nudge's scope.
  const showNudge = isNudgeActive && !isMobile && !isOpen;

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const goToUpgrade = () => navigate(`${RouteMap.PLAN}#${RouteMap.PLAN_ANCHOR}`);

  // Screen record runs through the extension; preserve the existing quota gate
  // (page buttons swapped to "Upgrade" when over quota).
  const handleScreenRecord = () => {
    handleClose();
    if (canCreateNote) {
      checkExtensionInstalled();
    } else {
      goToUpgrade();
    }
  };

  // Video/Audio navigate to the in-browser recording pages, matching the
  // sidebar Record menu's previous behavior (no quota gate at the entry point).
  const handleVideo = () => {
    handleClose();
    navigate(RouteMap.RECORD);
  };

  const handleAudio = () => {
    handleClose();
    navigate(RouteMap.AUDIO_RECORDING);
  };

  // Inherits the theme's pill radius (100px) on the desktop Button; the mobile
  // IconButton stays naturally circular. Matches NoteGo's button convention.
  const buttonSx = {
    bgcolor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': { bgcolor: theme.palette.primary.dark },
  };

  return (
    <>
      <Tooltip
        arrow
        open={showNudge}
        placement="bottom-end"
        title="Create your first note"
        slotProps={{
          tooltip: {
            sx: { bgcolor: 'primary.main', fontSize: 13, fontWeight: 600, px: 1.5, py: 1 },
          },
          arrow: { sx: { color: 'primary.main' } },
        }}
      >
        {isMobile ? (
          <IconButton
            data-testid="new-note-button"
            aria-label="New note"
            onClick={handleOpen}
            sx={{ ...buttonSx, width: 36, height: 36, mr: 1 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        ) : (
          <Button
            data-testid="new-note-button"
            onClick={handleOpen}
            startIcon={<AddIcon />}
            disableElevation
            sx={{
              ...buttonSx,
              fontWeight: 600,
              fontSize: '14px',
              px: 2,
              py: 0.75,
              mr: 2,
            }}
          >
            New Note
          </Button>
        )}
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ marginTop: '8px' }}
      >
        {!isMobile && (
          <MenuItem dense onClick={handleScreenRecord}>
            <ListItemIcon>
              <ScreenshotMonitorOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Screen Record</ListItemText>
          </MenuItem>
        )}
        <MenuItem dense onClick={handleVideo}>
          <ListItemIcon>
            <FaceOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Video</ListItemText>
        </MenuItem>
        <MenuItem dense onClick={handleAudio}>
          <ListItemIcon>
            <Mic fontSize="small" />
          </ListItemIcon>
          <ListItemText>Audio</ListItemText>
        </MenuItem>
      </Menu>

      <NewNoteModal open={isModalOpen} handleClose={() => setIsModalOpen(false)} onConfirm={onModalConfirm} />
    </>
  );
};

export default NewNoteButton;
